import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
    computed,
    inject,
    signal,
    effect,
    untracked,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogTitle, MatDialogClose } from '@angular/material/dialog';
import { ToastService } from '@app/core/services/toast.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import {
    CarrierDeliveryNoteInPool,
    LoginOrganizationSite,
    OrganizationSite,
} from 'api/models';
import {
    DeliveryNoteBundlesService,
    DeliveryNotesService,
    PlatformService,
} from 'api/services';
import { NgxSpinnerService } from 'ngx-spinner';
import { lastValueFrom, Subject } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

// Import configurable view dependencies
import {
    ViewConfig,
    ViewType,
    UserRole,
} from '@app/shared/models/view-config.interface';
import { TableConfiguration } from '@app/shared/models/generic-table.interface';
import { AppStore } from '@app/stores/appStore';
import { ViewConfigurationService } from '@app/shared/services/view-config.service';
import {
    SelectionStore,
    SelectableItem,
} from '@app/stores/selectionStore';

// Import data stores like configurable view
import {
    DeliveryNotesStore,
} from '@app/stores/deliveryNotesStore';
import { FoldersStore } from '@app/stores/foldersStore';

// Import view configs
import { SharePermissionsBundlesConfig } from '@app/shared/services/view-configs/carrier/share-permissions-bundles-config';
import { SharePermissionsDeliveryNotesConfig } from '@app/shared/services/view-configs/carrier/share-permissions-deliverynotes-config';
import { CarrierViewActionsService } from '@app/shared/services/view-configs/carrier-view-actions.service';

// Import paginated services
import { CarrierFoldersPaginatedService } from '@app/carrier/services/carrier-folders-paginated.service';
import { CarrierDeliveryNotesPaginatedService } from '@app/carrier/services/carrier-delivery-notes-paginated.service';

// Import search store functions
import { SearchStore } from '@app/stores/searchStore';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll, CdkVirtualForOf } from '@angular/cdk/scrolling';
import { MatButtonToggleGroup, MatButtonToggle } from '@angular/material/button-toggle';
import { GenericTableComponent } from '../../../shared/components/generic-table/generic-table.component';

export type SharePermissionsDialogProps = {
    organizationSite: LoginOrganizationSite;
    deliveryNoteBundles?: any[];
    deliveryNotes?: CarrierDeliveryNoteInPool[];
    initialView: 'bundles' | 'deliverySlip' | 'notes';
};

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-share-permissions-dialog',
    templateUrl: './share-permissions-dialog.component.html',
    styleUrls: ['./share-permissions-dialog.component.scss'],
    imports: [
        MatDialogTitle,
        MatIconButton,
        MatDialogClose,
        MatIcon,
        MatFormField,
        MatLabel,
        ReactiveFormsModule,
        MatAutocompleteModule,
        MatInputModule,
        CdkVirtualScrollViewport,
        CdkFixedSizeVirtualScroll,
        CdkVirtualForOf,
        MatButton,
        MatButtonToggleGroup,
        MatButtonToggle,
        GenericTableComponent,
        TranslateModule,
    ],
})
export class SharePermissionsDialogComponent implements OnInit, OnDestroy {
    private deliveryNotesService = inject(DeliveryNotesService);
    private ngxSpinnerService = inject(NgxSpinnerService);
    private toastService = inject(ToastService);
    private platformService = inject(PlatformService);
    private deliveryNoteBundlesService = inject(DeliveryNoteBundlesService);
    private translateService = inject(TranslateService);
    private carrierActionsService = inject(CarrierViewActionsService);
    private carrierFoldersPaginatedService = inject(
        CarrierFoldersPaginatedService
    );
    private carrierDeliveryNotesPaginatedService = inject(
        CarrierDeliveryNotesPaginatedService
    );
    private viewConfigurationService = inject(ViewConfigurationService);

    organizationSiteKey: string;
    private appStore = inject(AppStore);
    private searchStore = inject(SearchStore);
    private foldersStore = inject(FoldersStore);
    private deliveryNotesStore = inject(DeliveryNotesStore);
    private selectionStore = inject(SelectionStore);

    // Dialog-specific properties
    targetSites: OrganizationSite[] = [];
    showSuccessScreen = signal(false);
    sharedDeliveryNotes = signal<any[]>([]);
    selectedOrg = signal<OrganizationSite | null>(null);
    orgFilterCtrl = new FormControl<string | OrganizationSite>('');
    filteredOrgs = signal<OrganizationSite[]>([]);
    protected _onDestroy = new Subject<void>();

    // Configurable view logic (copied from ConfigurableViewComponent)
    userRole: UserRole = 'carrier';

    // Signals for reactive state (copied from configurable view)
    currentViewType = signal<ViewType>('folders');
    currentConfig = signal<ViewConfig | null>(null);
    organizationSiteKey$ = this.appStore.currentOrganizationSiteKey;
    selectedItems = computed(() => this.selectionStore.selectedItems());

    // Data computed from stores (copied from configurable view)
    data = computed(() => {
        const config = this.currentConfig();

        if (config?.type === 'deliverynotes') {
            return this.deliveryNotesStore.deliveryNotes() as SelectableItem[];
        } else if (config?.type === 'folders') {
            return this.foldersStore.folders() as SelectableItem[];
        }
        return [];
    });

    loading = computed(() => {
        const config = this.currentConfig();
        if (config?.type === 'deliverynotes') {
            return this.deliveryNotesStore.loading();
        } else if (config?.type === 'folders') {
            return this.foldersStore.loading();
        }
        return false;
    });

    error = computed(() => {
        const config = this.currentConfig();
        if (config?.type === 'deliverynotes') {
            return this.deliveryNotesStore.error();
        } else if (config?.type === 'folders') {
            return this.foldersStore.error();
        }
        return null;
    });

    tableColumns = computed(() => {
        const config = this.currentConfig();
        if (!config) return [];
        return config.tableConfig.columns;
    });

    /**
     * Auto-reloading this list underneath the user makes no sense while they
     * are picking recipients in the dialog. Must stay a stable instance: an
     * inline template literal would be a new reference on every change
     * detection run and keep restarting the table's config effect.
     */
    protected readonly tableConfig: TableConfiguration = {
        columns: [],
        autoReload: { enabled: false },
    };

    filterConfig = computed(() => {
        const config = this.currentConfig();
        return config ? config.filterConfig : null;
    });

    permissions = computed(() => {
        const config = this.currentConfig();
        return config ? config.permissions : null;
    });

    // View configurations
    bundlesConfig: ViewConfig;
    deliveryNotesConfig: ViewConfig;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        private data_props: SharePermissionsDialogProps
    ) {
        this.organizationSiteKey = data_props.organizationSite._key;

        // Initialize view configurations
        this.initializeViewConfigs();

        // Set initial view based on props
        if (data_props.initialView === 'bundles') {
            this.currentViewType.set('folders');
        } else if (
            data_props.initialView === 'deliverySlip' ||
            data_props.initialView === 'notes'
        ) {
            this.currentViewType.set('deliverynotes');
        } else {
            this.currentViewType.set('folders');
        }

        // Effect to update config when view changes (copied from configurable view)
        effect(() => {
            const viewType = this.currentViewType();
            const config = this.getViewConfig(viewType);
            this.currentConfig.set(config || null);
        });

        // Effect to handle config changes and data loading (copied from configurable view)
        effect(() => {
            const config = this.currentConfig();
            const orgSiteKey = this.organizationSiteKey$();

            if (config && orgSiteKey) {
                untracked(() => {
                    this.loadViewData();
                });
            }
        });

        // Set dialog state to open
        this.appStore.setSharePermissionsDialogOpen(true);
    }

    async ngOnInit() {
        this.getTargetOrgas();

        // Initialize selection context (like configurable view)
        this.selectionStore.setContextData({
            viewType: this.currentViewType(),
            userRole: this.userRole,
            organizationSiteKey: this.organizationSiteKey || undefined,
        });

        // Clear any previous selection
        this.selectionStore.clearSelection();

        this.orgFilterCtrl.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(() => this.filterOrgs());
    }

    ngOnDestroy() {
        this._onDestroy.next();
        this._onDestroy.complete();

        // Set dialog state to closed and trigger data refresh for main table
        this.appStore.setSharePermissionsDialogOpen(false);

        // Reset pagination before refreshing to ensure clean state
        this.searchStore.resetPagination();
        this.searchStore.triggerDataRefresh();
    }

    private initializeViewConfigs() {
        // Initialize view configurations
        this.bundlesConfig = SharePermissionsBundlesConfig.create({
            carrierFoldersPaginatedService: this.carrierFoldersPaginatedService,
            actionsService: this.carrierActionsService,
            appStore: this.appStore,
        });

        this.deliveryNotesConfig = SharePermissionsDeliveryNotesConfig.create({
            carrierDeliveryNotesPaginatedService:
                this.carrierDeliveryNotesPaginatedService,
            actionsService: this.carrierActionsService,
            appStore: this.appStore,
        });
    }

    private getViewConfig(viewType: ViewType): ViewConfig | null {
        switch (viewType) {
            case 'folders':
                return this.bundlesConfig;
            case 'deliverynotes':
                return this.deliveryNotesConfig;
            default:
                return null;
        }
    }

    // Data loading (copied from configurable view)
    private async loadViewData(): Promise<void> {
        const config = this.currentConfig();
        const orgSiteKey = this.organizationSiteKey$();

        if (!config || !orgSiteKey) {
            return;
        }

        try {
            // Just trigger the API call - data will be available from store (same as configurable view)
            await config.apiConfig.loadData();
        } catch (error) {
            this.toastService.error(
                this.translateService.instant('GENERAL.ERROR.LOADING_DATA')
            );
        }
    }

    // View switching - matches main app pattern
    onViewChanged(viewType: ViewType) {
        this.currentViewType.set(viewType);
        this.selectionStore.clearSelection(); // Clear selection when switching views

        // Use the same pattern as main app (paginated-table-filter component)
        const viewConfig = this.viewConfigurationService.getViewConfig(
            this.userRole,
            viewType
        );
        const searchConfig = viewConfig?.searchConfig;

        // This properly resets pagination and triggers data refresh
        this.searchStore.updateCurrentViewType(viewType, searchConfig);
    }

    // Selection handling (copied from configurable view)
    onSelectionChange(event: { selected: SelectableItem[] }) {
        this.selectionStore.setSelection(event.selected, {
            viewType: this.currentViewType(),
            userRole: this.userRole,
            organizationSiteKey: this.organizationSiteKey || undefined,
        });
    }

    onRefresh(): void {
        this.loadViewData();
    }

    // Dialog-specific methods (keep existing logic)
    protected filterOrgs() {
        if (!this.targetSites) {
            return;
        }

        let search = this.orgFilterCtrl.value;
        if (!search || typeof search !== 'string') {
            this.filteredOrgs.set(this.targetSites.slice());
            return;
        } else {
            search = search.toLowerCase();
        }

        this.filteredOrgs.set(
            this.targetSites.filter(
                org => org.name.toLowerCase().indexOf(search) > -1
            )
        );
    }

    get amountOfSelectedItems(): number {
        const selectedItems = this.selectionStore.selectedItems();
        return selectedItems.length;
    }

    async getTargetOrgas() {
        this.ngxSpinnerService.show();
        let sites = await lastValueFrom(
            this.platformService.getPlatformOrganisationsSites()
        );

        sites = sites.filter(site => site.carrier);
        this.targetSites = sites.filter(
            site => site._key !== this.organizationSiteKey
        );

        this.filteredOrgs.set(this.targetSites.slice());
        this.ngxSpinnerService.hide();
    }

    async sharePermission() {
        const org = this.selectedOrg();
        if (!org) {
            return;
        }
        this.ngxSpinnerService.show();

        const selectedItems = this.selectionStore.selectedItems();
        const collectedNotes: any[] = [];

        // Handle selected bundles - get all delivery notes in each bundle
        const bundleItems = selectedItems.filter(item =>
            item.hasOwnProperty('numberOfDeliveryNotes')
        );
        for (const bundle of bundleItems) {
            const bundleDetails = await lastValueFrom(
                this.deliveryNoteBundlesService.getCarrierBundle({
                    organizationSiteKey: this.organizationSiteKey,
                    deliveryNoteBundleKey: (bundle as any)._key,
                })
            );
            collectedNotes.push(...bundleDetails.deliveryNotes);
        }

        // Handle selected individual delivery notes
        const deliveryNoteItems = selectedItems.filter(
            item =>
                item.hasOwnProperty('number') &&
                !item.hasOwnProperty('numberOfDeliveryNotes')
        );
        collectedNotes.push(...deliveryNoteItems);

        this.sharedDeliveryNotes.set(collectedNotes);

        // Remove duplicates
        const uniqueKeys = [
            ...new Set(
                this.sharedDeliveryNotes().map(entry => (entry as any)._key)
            ),
        ];

        try {
            await lastValueFrom(
                this.deliveryNotesService.shareDeliveryNotesRights({
                    organizationSiteKey: this.organizationSiteKey,
                    body: {
                        targetOrganizationSiteKey: org._key,
                        deliveryNoteKeys: uniqueKeys,
                    },
                })
            );
            this.toastService.success(
                this.translateService.instant('SHARE-PERMISSIONS.SUCCESS')
            );

            this.sharedDeliveryNotes.set([...new Set(this.sharedDeliveryNotes())]);

            this.showSuccessScreen.set(true);
        } catch (error: HttpErrorResponse | any) {
            if (error.error.includes('already shared')) {
                this.toastService.error(
                    this.translateService.instant(
                        'SHARE-PERMISSIONS.ERROR-ALREADY-SHARED'
                    )
                );
            } else {
                this.toastService.error(
                    this.translateService.instant('SHARE-PERMISSIONS.ERROR')
                );
            }
        } finally {
            this.ngxSpinnerService.hide();
        }
    }
}
